import { Link, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/States";
import { CopyButton } from "@/components/ui/copy-button";
import { Snippet } from "./SettingsTokens";

export function SettingsNav() {
  const item = ({ isActive }: { isActive: boolean }) =>
    cn("rounded-md px-3 py-1.5 text-sm transition-colors", isActive ? "bg-card font-medium shadow-sm" : "text-muted-foreground hover:text-foreground");
  return (
    <nav className="mb-6 inline-flex rounded-lg bg-muted p-1" aria-label="Settings">
      <NavLink to="/settings/account" className={item}>
        Account
      </NavLink>
      <NavLink to="/settings/tokens" className={item}>
        API tokens
      </NavLink>
      <NavLink to="/settings/connect" className={item}>
        Connect a client
      </NavLink>
    </nav>
  );
}

export default function ConnectPage() {
  const origin = window.location.origin;
  const mcpUrl = `${origin}/api/mcp`;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Connect a client" description="Minne is an MCP server. Clients connect over streamable HTTP and sign in with OAuth 2.1." />
      <SettingsNav />

      <section className="mb-8 rounded-xl border bg-card p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">MCP server URL</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 font-mono text-sm">{mcpUrl}</code>
          <CopyButton text={mcpUrl} label="Copy URL" showLabel size="sm" variant="outline" />
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Clients discover the authorization server through <code className="font-mono text-xs">/.well-known/oauth-protected-resource</code> and register themselves (dynamic client registration), so there is nothing to pre-configure. You approve each client once on the consent page.
        </p>
      </section>

      <div className="grid gap-6">
        <Step n={1} title="Claude (claude.ai and desktop)">
          <p>
            Open <strong>Settings → Connectors → Add custom connector</strong>, paste the URL above as the remote MCP server URL and finish the sign-in. Claude will ask for permission on Minne's consent page.
          </p>
        </Step>

        <Step n={2} title="Claude Code (OAuth)">
          <p className="mb-2">Add the server, then run <code className="font-mono text-xs">/mcp</code> inside Claude Code to authenticate in the browser:</p>
          <Snippet code={`claude mcp add --transport http minne ${mcpUrl}`} />
        </Step>

        <Step n={3} title="Claude Code (static token)">
          <p className="mb-2">
            Shortcut without OAuth: create a token under{" "}
            <Link to="/settings/tokens" className="text-link underline-offset-2 hover:underline">
              API tokens
            </Link>{" "}
            and pass it as a header. The token name becomes the client name on notes it writes.
          </p>
          <Snippet code={`claude mcp add --transport http minne ${mcpUrl} --header "Authorization: Bearer mn_…"`} />
        </Step>

        <Step n={4} title="What the model gets">
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Tools: <code className="font-mono text-xs">search</code>, <code className="font-mono text-xs">read_note</code>, <code className="font-mono text-xs">create_note</code>, <code className="font-mono text-xs">patch_note</code>, <code className="font-mono text-xs">move_note</code>, <code className="font-mono text-xs">recent</code>, <code className="font-mono text-xs">save_thread</code>, <code className="font-mono text-xs">list_spaces</code>, <code className="font-mono text-xs">list_notes</code>.
            </li>
            <li>
              The component guide as a resource: <code className="font-mono text-xs">minne://guide/components</code>.
            </li>
            <li>Every write returns a thread id; the model passes it on later calls so the conversation shows up under Threads.</li>
          </ul>
        </Step>

        <Step n={5} title="Endpoints (for other clients)">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-mono text-xs">
            <dt className="text-muted-foreground">metadata</dt>
            <dd className="break-all">{origin}/.well-known/oauth-authorization-server</dd>
            <dt className="text-muted-foreground">resource</dt>
            <dd className="break-all">{origin}/.well-known/oauth-protected-resource</dd>
            <dt className="text-muted-foreground">register</dt>
            <dd>{origin}/register</dd>
            <dt className="text-muted-foreground">authorize</dt>
            <dd>{origin}/authorize</dd>
            <dt className="text-muted-foreground">token</dt>
            <dd>{origin}/token</dd>
          </dl>
        </Step>
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className="flex gap-4">
      <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{n}</span>
      <div className="min-w-0 flex-1">
        <h2 className="mb-1.5 text-base font-semibold">{title}</h2>
        <div className="text-sm leading-relaxed text-foreground/85">{children}</div>
      </div>
    </section>
  );
}
